import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { BannerMessageType } from 'src/environments/banner-environment';
import { environment } from 'src/environments/environment';
import { Animations } from '../../angular-animations/angular-animations';
import { SurveyBannerService } from '../../services/survey-banner.service';

const AUTO_TERMINATE_MS = 20000;
const MOBILE_BREAKPOINT = '(max-width: 799px)';
const OVERLAY_BREAKPOINT = '(min-width: 800px)';

@Component({
  selector: 'app-survey-banner',
  templateUrl: './survey-banner.component.html',
  styleUrls: ['./survey-banner.component.scss'],
  standalone: false,
  animations: [Animations.slideFromTop],
})
export class SurveyBannerComponent implements OnInit, OnDestroy {
  readonly BannerMessageType = BannerMessageType;
  readonly customMessage = environment.banner.customMessage;
  readonly customUrl = environment.banner.customUrl;
  readonly messageType = environment.banner.messageType;

  @HostBinding('class.survey-banner-host--overlay')
  isOverlayMode = false;

  @HostBinding('class.survey-banner-host--hidden')
  get isHidden(): boolean {
    return !this.isVisible;
  }

  isVisible = false;
  private autoTerminateTimeoutId?: ReturnType<typeof setTimeout>;
  private isAppendedToBody = false;
  private originalParent: HTMLElement | null = null;
  private originalNextSibling: ChildNode | null = null;
  private readonly subscription = new Subscription();

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private breakpointObserver: BreakpointObserver,
    private surveyBannerService: SurveyBannerService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isVisible = environment.banner.display && !this.surveyBannerService.isDismissed();
  }

  ngOnInit(): void {
    this.removeOrphanedBannerHosts();

    this.subscription.add(
      this.breakpointObserver.observe([MOBILE_BREAKPOINT, OVERLAY_BREAKPOINT]).subscribe(() => {
        this.updateOverlayPlacement();
      })
    );

    this.updateOverlayPlacement();

    if (!this.isVisible || !environment.banner.autoTerminate) {
      return;
    }

    this.autoTerminateTimeoutId = setTimeout(() => this.dismiss(), AUTO_TERMINATE_MS);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.clearAutoTerminateTimeout();

    if (this.isAppendedToBody) {
      this.removeHostFromDom();
    }
  }

  openSurvey(): void {
    if (!this.customUrl) {
      return;
    }

    window.open(this.customUrl, '_blank');
  }

  dismiss(): void {
    this.surveyBannerService.dismiss();
    this.isVisible = false;
    this.updateOverlayPlacement();
    this.clearAutoTerminateTimeout();
  }

  private updateOverlayPlacement(): void {
    if (this.breakpointObserver.isMatched(MOBILE_BREAKPOINT)) {
      this.isOverlayMode = false;

      if (this.isAppendedToBody) {
        this.restoreToOriginalParent();
      }

      return;
    }

    const shouldUseOverlay =
      this.isVisible && this.breakpointObserver.isMatched(OVERLAY_BREAKPOINT);
    this.isOverlayMode = shouldUseOverlay;

    if (shouldUseOverlay) {
      this.appendToBody();
      return;
    }

    this.restoreToOriginalParent();
  }

  private removeOrphanedBannerHosts(): void {
    const host = this.elementRef.nativeElement;

    this.document.querySelectorAll('app-survey-banner').forEach((element) => {
      if (element !== host && element.parentElement === this.document.body) {
        element.remove();
      }
    });
  }

  private removeHostFromDom(): void {
    const host = this.elementRef.nativeElement;

    if (host.parentElement) {
      this.renderer.removeChild(host.parentElement, host);
    }

    this.isAppendedToBody = false;
  }

  private appendToBody(): void {
    if (this.isAppendedToBody) {
      return;
    }

    const host = this.elementRef.nativeElement;
    this.originalParent = host.parentElement;
    this.originalNextSibling = host.nextSibling;
    this.renderer.appendChild(this.document.body, host);
    this.isAppendedToBody = true;
  }

  private restoreToOriginalParent(): void {
    if (!this.isAppendedToBody || !this.originalParent) {
      return;
    }

    const host = this.elementRef.nativeElement;
    this.renderer.insertBefore(this.originalParent, host, this.originalNextSibling);
    this.isAppendedToBody = false;
  }

  private clearAutoTerminateTimeout(): void {
    if (!this.autoTerminateTimeoutId) {
      return;
    }

    clearTimeout(this.autoTerminateTimeoutId);
    this.autoTerminateTimeoutId = undefined;
  }
}
